import { Card } from '@ui/themed';
import { t } from 'i18next';
import { cn, SHIMMER_CLASSES } from '../../../lib/styles';
import { PLAN_KEYS } from '../constants';
import type { BillingPlan } from '../types/plan';
import { CurrentPlanCard } from './current-plan-card';
import { PlanCard } from './plan-card';

export type PlansGridProps = {
  availablePlans: BillingPlan[];
  activePlanKey?: string;
  canTrial?: boolean;
  hideEnterprise?: boolean;
};

const gridCn = (isEvenElements: boolean) =>
  cn('grid grid-cols-1 gap-6', isEvenElements ? 'md:grid-cols-2 xl:grid-cols-4' : 'lg:grid-cols-3');

function getNonActiveAvailablePlans(
  availablePlans: BillingPlan[],
  {
    activePlan,
    hideEnterprise,
  }: Pick<PlansGridProps, 'hideEnterprise'> & {
    activePlan: BillingPlan | undefined;
  },
): BillingPlan[] {
  return availablePlans.filter(
    (plan) =>
      !(
        activePlan?.planKey === plan.planKey ||
        (hideEnterprise && plan.planKey === PLAN_KEYS.enterprise)
      ),
  );
}

export function PlansGrid({
  availablePlans,
  activePlanKey,
  canTrial,
  hideEnterprise,
}: PlansGridProps) {
  const activePlan = availablePlans.find((plan) => plan.planKey === activePlanKey);

  const nonActivePlans = getNonActiveAvailablePlans(availablePlans, {
    activePlan,
    hideEnterprise,
  });

  const showEnterprise =
    !hideEnterprise && availablePlans.some((plan) => plan.planKey === PLAN_KEYS.enterprise);

  function bulletsHeading(plan: BillingPlan): string {
    const fullIndex = availablePlans.findIndex((p) => p.planKey === plan.planKey);
    if (fullIndex <= 0) {
      return t('features:billing.components.plan_card.bullets.highlights_include');
    }
    return t('features:billing.components.plan_card.bullets.everything_in_plus', {
      previousPlan: availablePlans[fullIndex - 1]?.name,
    });
  }

  const isEven = nonActivePlans.length % 2 === 0;

  return (
    <div className="flex w-full flex-col gap-6">
      {activePlan && (
        <CurrentPlanCard plan={activePlan} bulletsHeading={bulletsHeading(activePlan)} />
      )}
      <div className={gridCn(isEven)}>
        {nonActivePlans.map((plan) => {
          const isProWithoutEnterprise = plan.planKey === PLAN_KEYS.v2Pro && !showEnterprise;
          return (
            <PlanCard
              key={plan.planKey}
              planKey={plan.planKey}
              planName={plan.name}
              cost={plan.price}
              description={plan.description}
              bulletsHeading={bulletsHeading(plan)}
              bullets={plan.points}
              canTrial={canTrial}
              currentPlanKey={activePlanKey}
              showNeedMore={isProWithoutEnterprise}
            />
          );
        })}
      </div>
    </div>
  );
}

export function PlansGridSkeleton({ showEnterprise }: { showEnterprise?: boolean }) {
  const count = showEnterprise ? 4 : 3;
  const isEven = count % 2 === 0;

  return (
    <div className={gridCn(isEven)}>
      {Array.from({ length: count }).map((_, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
        <Card key={index}>
          <Card.Body>
            <div className="flex min-h-100 flex-col gap-6">
              <div className="flex flex-col gap-2">
                <div className={cn('h-4 w-24 rounded', SHIMMER_CLASSES)} />
                <div className={cn('h-9 w-28 rounded', SHIMMER_CLASSES)} />
                <div className={cn('h-4 w-40 rounded', SHIMMER_CLASSES)} />
              </div>
              <div className={cn('h-10 w-full rounded', SHIMMER_CLASSES)} />
              <div className={cn('h-10 w-40 rounded', SHIMMER_CLASSES)} />
              <div className="flex flex-col gap-2">
                <div className={cn('h-4 w-32 rounded', SHIMMER_CLASSES)} />
                <div className={cn('h-3 w-full rounded', SHIMMER_CLASSES)} />
                <div className={cn('h-3 w-5/6 rounded', SHIMMER_CLASSES)} />
                <div className={cn('h-3 w-4/6 rounded', SHIMMER_CLASSES)} />
              </div>
            </div>
          </Card.Body>
        </Card>
      ))}
    </div>
  );
}
